import React, { useState } from 'react';
import { X, Calendar, ChevronDown, ChevronRight, PlayCircle } from 'lucide-react';
import { FaCalendarAlt } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useStrategyCalls } from '../../hooks/useStrategyCalls';
import { useStrategyCallBooking } from '../../hooks/useStrategyCallBooking';
import { getPillarScheduleLink } from '../../services/pillarsService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { taskTimeDepartmentLabel } from '../../utils/taskTimeConstants';
import MeetingSummaryContent from '../MeetingSummaryContent';

function formatCallDate(date) {
  if (!date) return '—';
  try {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

const DepartmentCallsModal = ({ isOpen, onClose, department, calls = [], fallbackScheduleUrl }) => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { used, limit, loading: callsLoading, atLimit } = useStrategyCalls();
  const [expandedId, setExpandedId] = useState(null);
  const [scheduleLink, setScheduleLink] = useState('');
  const [userData, setUserData] = useState(null);

  const departmentLabel = taskTimeDepartmentLabel(department);

  const { openBooking, meetingAgendaModal } = useStrategyCallBooking({
    currentUser,
    userProfile: userData,
    disabled: atLimit,
    variant: 'department',
    requireFocus: true,
  });

  React.useEffect(() => {
    if (!isOpen || !currentUser) return;
    (async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) setUserData(userDoc.data());
        const link = await getPillarScheduleLink(currentUser.uid, department);
        setScheduleLink(link);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [isOpen, currentUser, department]);

  if (!isOpen) return null;

  const openSchedule = () => {
    if (atLimit) return;
    const url = scheduleLink || fallbackScheduleUrl;
    if (!url) return;
    const isCalendly = /calendly\.com/i.test(url);
    openBooking({
      url,
      fallbackUrl: fallbackScheduleUrl,
      useCalendly: !!scheduleLink || isCalendly,
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div
          className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col`}
        >
          <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {departmentLabel} Calls
              </h2>
              {!callsLoading && used != null && limit != null && (
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {used} / {limit} strategy calls this period
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`p-1 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {calls.length === 0 ? (
              <p className={`text-sm text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No {departmentLabel.toLowerCase()} calls recorded yet.
              </p>
            ) : (
              calls.map((call) => {
                const isExpanded = expandedId === call.id;
                const hasDetails = !!call.summary || !!call.recordingUrl;
                return (
                  <div
                    key={call.id}
                    className={`rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <button
                      type="button"
                      className="w-full flex items-start gap-2 p-3 text-left"
                      onClick={() => setExpandedId(isExpanded ? null : call.id)}
                      disabled={!hasDetails}
                    >
                      {hasDetails ? (
                        isExpanded ? (
                          <ChevronDown className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                        )
                      ) : (
                        <Calendar className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {call.title}
                        </p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatCallDate(call.date)} · {call.status}
                        </p>
                      </div>
                      {call.recordingUrl && (
                        <PlayCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
                      )}
                    </button>
                    {isExpanded && hasDetails && (
                      <div className="px-3 pb-3 space-y-3">
                        {call.recordingUrl && (
                          <a
                            href={call.recordingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-700 text-white"
                          >
                            <PlayCircle className="w-3.5 h-3.5" />
                            Watch recording
                          </a>
                        )}
                        {call.summary && <MeetingSummaryContent markdown={call.summary} />}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              type="button"
              onClick={openSchedule}
              disabled={atLimit}
              className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                atLimit
                  ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
            >
              <FaCalendarAlt />
              {atLimit ? 'Strategy Calls Unavailable' : 'Schedule a Call'}
            </button>
          </div>
        </div>
      </div>
      {meetingAgendaModal}
    </>
  );
};

export default DepartmentCallsModal;
